import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { rendimientoService } from '../../../services/rendimientoService';

const FotografiaSection = ({ loncheadoId, initialFotos = [], onFotosChange, fotos: fotosProp, onChange }) => {
  // Support both old and new prop patterns
  const [fotos, setFotos] = useState(fotosProp || initialFotos);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    const source = fotosProp || initialFotos;
    if (source?.length > 0) setFotos(source);
  }, [fotosProp?.length, initialFotos?.length]);

  const notifyChange = (newFotos) => {
    onFotosChange?.(newFotos);
    onChange?.(newFotos);
  };

  const handleFiles = async (files) => {
    if (!files || files?.length === 0) return;
    setUploading(true);
    setUploadError(null);
    const newFotos = [...fotos];

    for (const file of Array.from(files)) {
      if (file?.size > 10 * 1024 * 1024) {
        setUploadError(`"${file?.name}" supera el límite de 10 MB.`);
        continue;
      }
      if (loncheadoId) {
        try {
          const { publicUrl, filePath } = await rendimientoService?.uploadFoto(loncheadoId, file);
          newFotos?.push({ url: publicUrl, path: filePath, name: file?.name, remote: true });
        } catch (err) {
          setUploadError(`Error al subir "${file?.name}": ${err?.message}`);
        }
      } else {
        try {
          const localUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          newFotos?.push({ url: localUrl, name: file?.name, remote: false, file });
        } catch (err) {
          setUploadError(`Error al procesar "${file?.name}": ${err?.message}`);
        }
      }
    }

    setFotos(newFotos);
    notifyChange(newFotos);

    if (loncheadoId) {
      try {
        const remoteUrls = newFotos?.filter(f => f?.remote)?.map(f => f?.url);
        await rendimientoService?.saveFotoUrl(loncheadoId, remoteUrls);
      } catch {}
    }
    setUploading(false);
  };

  const handleDelete = async (index) => {
    const foto = fotos?.[index];
    try {
      if (foto?.remote && foto?.path) await rendimientoService?.deleteFoto(foto?.path);
      const newFotos = fotos?.filter((_, i) => i !== index);
      setFotos(newFotos);
      notifyChange(newFotos);
      if (loncheadoId) {
        const remoteUrls = newFotos?.filter(f => f?.remote)?.map(f => f?.url);
        await rendimientoService?.saveFotoUrl(loncheadoId, remoteUrls);
      }
    } catch (err) {
      setUploadError(`Error al eliminar fotografía: ${err?.message}`);
    }
  };

  const handleDownload = (foto) => {
    const a = document.createElement('a');
    a.href = foto?.url;
    a.download = foto?.name || 'foto-pieza.jpg';
    a.target = '_blank';
    a?.click();
  };

  const handleDrop = (e) => { e?.preventDefault(); handleFiles(e?.dataTransfer?.files); };
  const handleDragOver = (e) => { e?.preventDefault(); };

  return (
    <div>
      {/* Info banner when no record saved yet */}
      {!loncheadoId && (
        <div className="mb-3 p-2.5 bg-[#8097D6]/8 border border-[#8097D6]/20 rounded-lg flex items-start gap-2">
          <Icon name="Info" size={13} style={{ color: '#8097D6', flexShrink: 0, marginTop: 1 }} />
          <p className="text-[11px] font-oranienbaum text-white">
            Las fotos se subirán al bucket <strong className="text-white">jamones</strong> automáticamente al guardar el registro.
          </p>
        </div>
      )}
      {uploadError && (
        <div className="mb-3 p-2.5 bg-[#E05252]/8 border border-[#E05252]/20 rounded-lg flex items-start gap-2">
          <Icon name="AlertCircle" size={13} style={{ color: '#E05252', flexShrink: 0, marginTop: 1 }} />
          <p className="text-[11px] font-oranienbaum text-[#E05252] flex-1">{uploadError}</p>
          <button onClick={() => setUploadError(null)} className="text-white/30 hover:text-white/60 transition-colors">
            <Icon name="X" size={12} />
          </button>
        </div>
      )}
      {/* Upload area */}
      <div
        className="border border-dashed border-white/15 rounded-lg p-4 text-center hover:border-[#D6EF4E]/30 transition-colors cursor-pointer mb-3 bg-[#111111]"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef?.current?.click()}
      >
        <Icon name="Upload" size={22} style={{ color: 'rgba(255,255,255,0.25)', margin: '0 auto 8px' }} />
        <p className="text-xs font-oranienbaum text-white mb-0.5">Arrastra fotos aquí o haz clic para seleccionar</p>
        <p className="text-[10px] font-oranienbaum text-white/60">JPG, PNG, WEBP · Máx. 10 MB por imagen</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e?.target?.files)}
        />
      </div>
      {/* Action buttons */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => cameraInputRef?.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-oranienbaum font-medium transition-colors flex-1 justify-center text-[19px]"
          style={{ background: 'rgba(214,239,78,0.08)', color: '#D6EF4E', border: '1px solid rgba(214,239,78,0.15)' }}
        >
          <Icon name="Camera" size={13} />
          Tomar Fotografía
        </button>
        <button
          type="button"
          onClick={() => fileInputRef?.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg font-oranienbaum font-medium transition-colors flex-1 justify-center text-[17px] text-[rgba(214,239,78,1)]"
          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <Icon name="FolderOpen" size={13} />
          Subir Archivo
        </button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(e?.target?.files)}
        />
      </div>
      {uploading && (
        <div className="flex items-center gap-2 text-xs font-oranienbaum text-[#D6EF4E]/70 mb-3">
          <div className="w-3.5 h-3.5 border-2 border-[#D6EF4E]/30 border-t-[#D6EF4E] rounded-full animate-spin" />
          Subiendo fotografía...
        </div>
      )}
      {/* Photo grid */}
      {fotos?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {fotos?.map((foto, index) => (
            <div key={index} className="relative group rounded-lg overflow-hidden border border-white/8 bg-[#111111] aspect-square">
              <img
                src={foto?.url}
                alt={foto?.name || `Fotografía ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
              />
              {!foto?.remote && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-oranienbaum font-semibold"
                  style={{ background: '#C9A24D', color: '#000' }}>Local</div>
              )}
              {foto?.remote && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-oranienbaum font-semibold"
                  style={{ background: '#3A7F6B', color: '#fff' }}>Guardada</div>
              )}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                <button type="button" onClick={() => setLightboxUrl(foto?.url)}
                  className="p-1.5 rounded-full transition-colors" style={{ background: 'rgba(255,255,255,0.15)' }} title="Ampliar">
                  <Icon name="ZoomIn" size={13} style={{ color: '#fff' }} />
                </button>
                <button type="button" onClick={() => handleDownload(foto)}
                  className="p-1.5 rounded-full transition-colors" style={{ background: 'rgba(255,255,255,0.15)' }} title="Descargar">
                  <Icon name="Download" size={13} style={{ color: '#fff' }} />
                </button>
                <button type="button" onClick={() => handleDelete(index)}
                  className="p-1.5 rounded-full transition-colors" style={{ background: 'rgba(224,82,82,0.7)' }} title="Eliminar">
                  <Icon name="Trash2" size={13} style={{ color: '#fff' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh] w-full mx-4" onClick={(e) => e?.stopPropagation()}>
            <img src={lightboxUrl} alt="Fotografía ampliada" className="w-full h-full object-contain rounded-xl" />
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute top-3 right-3 p-2 rounded-full transition-colors"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            >
              <Icon name="X" size={16} style={{ color: '#fff' }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FotografiaSection;
